import React, { useEffect, useState } from "react";
import { Row, Card, Col, Spinner, Button } from "react-bootstrap";
import styles from "../onboard.module.css";
import FileUploader from "./fileUpload";
import { documentUploadApi } from "../../../api";
import { useMyContext } from "../../../myContext";
import greentickmark from "../../signup/assets/greenTickIcon.svg";
import CommonButton from "../../CommonButton/CommonButton";

const StudentDocUploads = ({
  stepToShow,
  setStepsToShow,
  setShowPassportDetail,
  showPassportDetail,
  reqDocs,
  uploadError,
  setUploadError,
}) => {
  const [loading, setLoading] = useState(false);
  const [passportFrontFile, setPassportFront] = useState(null);
  const [passportBackFile, setPassportBack] = useState(null);
  const [passportFrontFileType, setPassportFrontFileType] = useState(null);
  const [passportBackFileType, setPassportBackFileType] = useState(null);
  const [uploadPassport, setUploadPassport] = useState(false);

  // PAN card state variables
  const [panFile, setPanFile] = useState(null);
  const [panFileType, setPanFileType] = useState(null);
  const [uploadPAN, setUploadPAN] = useState(false);

  const {
    studentUserId,
    setCompletedStudent,
    completedRemitter,
    completedBeneficiary,
    completedStudent,
    showStudentDetail,
    showStudentUpload,
  } = useMyContext("");

  const acceptedFormats = ".jpeg, .jpg, .png .pdf";

  useEffect(() => {
    if (reqDocs.length === 0) {
      setUploadPassport(true);
      setUploadPAN(false);
    } else {
      setUploadPassport(reqDocs.includes("Passport"));
      setUploadPAN(reqDocs.includes("PAN"));
    }
  }, [reqDocs]);

  useEffect(() => {
    if (
      !completedStudent &&
      completedRemitter &&
      completedBeneficiary &&
      showStudentDetail &&
      showStudentUpload &&
      !showPassportDetail
    ) {
      setStepsToShow("StudentStep2");
    }
  }, [completedRemitter, completedBeneficiary]);

  const checkValidFile = (files) => {
    return files.some(
      (file) =>
        acceptedFormats.includes(file.type) ||
        acceptedFormats.includes(`.${file.name.split(".").pop()}`)
    );
  };

  const handleFrontPassportDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");
      setPassportFront(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setPassportFrontFileType(fileExtension);
    }
  };

  const handleBackPassportDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");
      setPassportBack(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setPassportBackFileType(fileExtension);
    }
  };

  // PAN card drop handler
  const handlePanDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");
      setPanFile(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setPanFileType(fileExtension);
    }
  };

  const onRemoveButton = (label) => {
    if (label === "Front of Passport") {
      setPassportFront(null);
      setPassportFrontFileType("");
    } else if (label === "Back of Passport") {
      setPassportBack(null);
      setPassportBackFileType("");
    } else if (label === "PAN Card") {
      setPanFile(null);
      setPanFileType("");
    }
  };

  const checkIfValid = async () => {
    setUploadError("");
    const isPassportValid = uploadPassport
      ? passportBackFile !== null && passportFrontFile !== null
      : true;
    const isPanValid = uploadPAN ? panFile !== null : true;

    if (isPassportValid && isPanValid) {
      try {
        setLoading(true);
        const uploadPromises = [];

        if (uploadPassport) {
          uploadPromises.push(frontPassportApi());
          uploadPromises.push(backPassportApi());
        }
        if (uploadPAN) {
          uploadPromises.push(panApi());
        }

        if (uploadPromises.length > 0) {
          Promise.all(uploadPromises)
            .then((res) => {
              const errors = [];
              res.forEach((element) => {
                if (element?.status === 400) {
                  errors.push(
                    element.data?.message || "An error occurred during upload"
                  );
                }
              });
              if (errors.length > 0) {
                const errorMessage = errors.join("\n");
                setUploadError(errorMessage);
                setLoading(false);
                return;
              }

              setLoading(false);
              setStepsToShow("BeneficiaryStep1");
              setCompletedStudent(true);
              setShowPassportDetail(true);
            })
            .catch((error) => {
              setLoading(false);
              setUploadError(
                error ? error : "Something went wrong, please try again later."
              );
            });
        } else {
          setLoading(false);
          setStepsToShow("BeneficiaryStep1");
          setCompletedStudent(true);
          setShowPassportDetail(true);
        }
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const uploadFile = async (file, fileType, docType, side) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await documentUploadApi(
        studentUserId,
        fileType,
        docType,
        side,
        formData
      );
      return response;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  const frontPassportApi = async () => {
    const res = await uploadFile(
      passportFrontFile,
      passportFrontFileType,
      "PASSPORT",
      "front"
    );
    return res;
  };

  const backPassportApi = async () => {
    const res = await uploadFile(
      passportBackFile,
      passportBackFileType,
      "PASSPORT",
      "back"
    );
    return res;
  };

  const panApi = async () => {
    const res = await uploadFile(panFile, panFileType, "PAN", "front");
    return res;
  };

  const handleError = (error) => {
    setUploadError("Something went wrong, please try again later.");
  };

  return (
    <Card className={styles.cardAccount}>
      <Card.Body className={styles.cardBodyAccount}>
        <Row className={styles.Title}>Document Upload</Row>
        {showPassportDetail === true && (
          <>
            <Row>
              <Col md={11} sm={11} xs={10} className={styles.details}>
                Successfully Uploaded!
              </Col>
              <Col md={1} sm={1} xs={2} style={{ position: "relative" }}>
                <img className={styles.greentick} src={greentickmark} alt="" />
              </Col>
            </Row>
          </>
        )}
        {stepToShow === "StudentStep2" ? (
          <>
            <hr style={{ marginTop: "0px" }} />
            <div style={{ marginTop: "20px" }}>
              <Row style={{ marginBottom: "8px", justifyContent: "center" }}>
                {/* PAN Upload */}
                {uploadPAN && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handlePanDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="PAN Card"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("PAN Card")}
                    />
                  </Col>
                )}
                {(reqDocs.length === 0 ||
                  (reqDocs.length !== 0 &&
                    reqDocs.find((item) => item === "Passport"))) && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handleFrontPassportDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="Front of Passport"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("Front of Passport")}
                    />
                  </Col>
                )}
                {(reqDocs.length === 0 ||
                  (reqDocs.length !== 0 &&
                    reqDocs.find((item) => item === "Passport"))) && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handleBackPassportDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="Back of Passport"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("Back of Passport")}
                    />
                  </Col>
                )}
              </Row>
            </div>
            <Row className={styles.allowedFormat}>
              Allowed Formats are jpeg, png, jpg, pdf and the size should be
              less then 5MB
            </Row>
            <Row className="pt-3 mt-3">
              {uploadError !== "" && (
                <div className={`${styles.errorState} ${styles.uploaderror}`}>
                  {uploadError}
                </div>
              )}
              <Col md={3}>
                <CommonButton
                  title="Next"
                  onClick={checkIfValid}
                  loading={loading}
                  disabled={
                    loading ||
                    !(uploadPassport
                      ? passportBackFile !== null && passportFrontFile !== null
                      : true) ||
                    !(uploadPAN ? panFile !== null : true) ||
                    uploadError !== ""
                  }
                  width={"150px"}
                />
                {/* <Button
                  className={styles.button}
                  onClick={checkIfValid}
                  disabled={loading || !((uploadPassport ? (passportBackFile !== undefined && passportFrontFile !== undefined) : true))}
                  style={{background: themeColor}}
                >
                  {loading && (
                    <Spinner
                      animation="border"
                      className={styles.spinnerColor}
                      style={{borderColor: themeColor, borderRightColor: "white"}}
                    />
                  )}
                  Next
                </Button> */}
              </Col>
            </Row>
          </>
        ) : (
          ""
        )}
      </Card.Body>
    </Card>
  );
};

export default StudentDocUploads;
